import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { useTheme } from "../context/ThemeContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Minus, Trash2, ShoppingCart, Receipt as RcpIcon, UserPlus, Package, Layers, Loader2, Camera, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { VariantPicker, cartKey } from "../components/VariantPicker";
import { DateRangePicker } from "../components/DateRangePicker";
import { DpInput, calcDp } from "../components/DpInput";
import { useOnline } from "../lib/useOnline";
import { addToQueue, getQueue } from "../lib/offlineQueue";

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function POS() {
  const nav = useNavigate();
  const { settings } = useTheme();
  const online = useOnline();
  const sources = settings?.acquisition_sources || [];
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useCached("customers", []);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("Semua");
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [start, setStart] = useState(new Date().toISOString().slice(0,10));
  const [end, setEnd] = useState(new Date(Date.now()+86400000).toISOString().slice(0,10));
  const [deposit, setDeposit] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("rupiah");
  const [dpValue, setDpValue] = useState(0);
  const [dpType, setDpType] = useState("rupiah");
  const [notes, setNotes] = useState("");
  const [q, setQ] = useState("");
  const [variantProduct, setVariantProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [custSubmitting, setCustSubmitting] = useState(false);

  const [quickOpen, setQuickOpen] = useState(false);
  const emptyCust = { full_name: "", whatsapp: "", address: "", acquisition_source: "", id_number: "", id_photo: "", social_media: "", maps_link: "", referred_by_code: "" };
  const [newCust, setNewCust] = useState(emptyCust);
  const [depositOpen, setDepositOpen] = useState(false);
  const custFileRef = useRef();
  const onCustPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setNewCust(prev => ({ ...prev, id_photo: reader.result }));
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    getQueue().then(q => {
      const pend = q.filter(r => r.op === "create_customer").map(r => ({
        id: r.temp_id, full_name: r.payload.full_name, whatsapp: r.payload.whatsapp,
        address: r.payload.address, acquisition_source: r.payload.acquisition_source, offline: true,
      }));
      if (pend.length) setCustomers(prev => { const ids = new Set(prev.map(c => c.id)); return [...prev, ...pend.filter(c => !ids.has(c.id))]; });
    }).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/products?start=${start}&end=${end}`).then(r => setProducts(r.data)).catch(() => {});
  }, [start, end]);

  const UNCAT = "Tanpa Kategori";
  const chipList = useMemo(() => {
    const set = new Set(categories.map(c => c.name));
    products.forEach(p => { if (p.category) set.add(p.category); });
    const arr = Array.from(set).sort();
    const hasUncat = products.some(p => !p.category);
    return ["Semua", ...arr, ...(hasUncat ? [UNCAT] : [])];
  }, [categories, products]);

  const days = useMemo(() => {
    const s = new Date(start), e = new Date(end);
    return Math.max(1, Math.round((e-s)/86400000));
  }, [start, end]);

  const subtotal = cart.reduce((a,i) => a + i.daily_price * i.quantity * days, 0);
  const discAmount = discountType === "persen" ? subtotal * (Number(discount)||0) / 100 : Number(discount)||0;
  const total = Math.max(0, subtotal - discAmount);
  const { dp, remaining } = calcDp(total, dpValue, dpType);

  const pushToCart = (p, v) => {
    const key = cartKey(p.id, v?.id);
    setCart(prev => {
      const exist = prev.find(i => i.key === key);
      if (exist) return prev.map(i => i.key === key ? {...i, quantity: i.quantity + 1} : i);
      return [...prev, {
        key, product_id: p.id, product_name: p.name,
        daily_price: v?.daily_price || p.daily_price, quantity: 1,
        variant_id: v?.id || "", variant_name: v?.name || "",
        image_url: v?.image_url || p.image_url || "",
      }];
    });
  };
  const add = (p) => {
    if (p.available <= 0) return toast.error("Stok habis");
    if (p.variants?.length) { setVariantProduct(p); return; }
    pushToCart(p, null);
  };
  const changeQty = (key, delta) => setCart(prev => prev.map(i => i.key === key ? {...i, quantity: Math.max(1, i.quantity + delta)} : i));
  const remove = (key) => setCart(prev => prev.filter(i => i.key !== key));

  const quickCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCust.full_name.trim()) return toast.error("Nama wajib diisi");
    if (!newCust.acquisition_source) return toast.error("Pilih 'Tahu dari mana?'");
    if (custSubmitting) return;
    setCustSubmitting(true);
    try {
      if (!navigator.onLine) {
        const tempId = "localcust-" + uuid();
        await addToQueue({ local_id: "custq-" + tempId, op: "create_customer", temp_id: tempId,
          payload: { ...newCust, client_id: tempId }, status: "pending", created_at: new Date().toISOString() });
        setCustomers(prev => [...prev, { id: tempId, full_name: newCust.full_name, whatsapp: newCust.whatsapp,
          address: newCust.address, acquisition_source: newCust.acquisition_source, offline: true }]);
        setCustomerId(tempId);
        toast.success("Pelanggan disimpan offline · akan tersinkron otomatis");
      } else {
        const r = await api.post("/customers", newCust);
        toast.success("Pelanggan ditambahkan");
        setCustomers(prev => [...prev, r.data]);
        setCustomerId(r.data.id);
      }
      setQuickOpen(false);
      setNewCust(emptyCust);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setCustSubmitting(false); }
  };

  const doCheckout = async () => {
    if (submitting) return;
    if (!customerId) return toast.error("Pilih atau tambahkan pelanggan dulu");
    if (cart.length === 0) return toast.error("Keranjang kosong");
    setSubmitting(true);
    const clientId = uuid();
    const itemsPayload = cart.map(({ key, ...i }) => i);
    const payload = {
      customer_id: customerId, items: itemsPayload,
      start_date: start, end_date: end,
      deposit: Number(deposit)||0, discount: Number(discount)||0, discount_type: discountType,
      dp_value: Number(dpValue)||0, dp_type: dpType, notes, client_id: clientId,
    };

    const saveOffline = async () => {
      const localId = "local-" + clientId;
      const cust = customers.find(c => c.id === customerId) || {};
      const localRental = {
        id: localId, client_id: clientId,
        code: "OFFLINE-" + clientId.slice(0, 4).toUpperCase(),
        customer_id: customerId, customer_name: cust.full_name || "-", customer_whatsapp: cust.whatsapp || "",
        items: itemsPayload, start_date: start, end_date: end, days,
        subtotal, discount: Number(discount)||0, discount_type: discountType, discount_amount: discAmount,
        total, deposit: Number(deposit)||0, dp_value: Number(dpValue)||0, dp_type: dpType, dp_amount: dp, remaining,
        acquisition_source: cust.acquisition_source || "", notes,
        status: "reserved", created_at: new Date().toISOString(), offline: true,
      };
      await addToQueue({ local_id: localId, op: "create_rental", client_id: clientId, payload, receipt: localRental, status: "pending", created_at: localRental.created_at });
      toast.success("Tersimpan offline · akan tersinkron otomatis saat online");
      nav(`/receipt/${localId}`);
    };

    const isTempCust = String(customerId).startsWith("localcust-");
    try {
      if (!navigator.onLine || isTempCust) { await saveOffline(); return; }
      const r = await api.post("/rentals", payload);
      toast.success("Transaksi tersimpan: " + r.data.code);
      nav(`/receipt/${r.data.id}`);
    } catch (err) {
      if (!err.response) { await saveOffline(); }   // network dropped mid-request
      else { toast.error(err.response?.data?.detail || "Gagal transaksi"); }
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = products.filter(p => {
    const catOk = activeCat === "Semua" ? true : activeCat === UNCAT ? !p.category : p.category === activeCat;
    const searchOk = p.name.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase());
    return catOk && searchOk;
  });
  const custFiltered = customers.filter(c => c.full_name.toLowerCase().includes(custSearch.toLowerCase()) || (c.whatsapp||"").includes(custSearch));

  return (
    <div className="space-y-6" data-testid="pos-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">POS Kasir</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Transaksi Baru</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Input data-testid="pos-search" placeholder="Cari alat..." value={q} onChange={e => setQ(e.target.value)} />
          <div className="flex gap-2 overflow-x-auto pos-scroll -mx-1 px-1 py-1" data-testid="pos-cat-chips">
            {chipList.map(c => (
              <button key={c} type="button" data-testid={`pos-cat-chip-${c}`} onClick={() => setActiveCat(c)}
                className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors whitespace-nowrap"
                style={activeCat === c
                  ? { background: "var(--brand-primary)", color: "#fff", borderColor: "var(--brand-primary)" }
                  : { background: "#fff", color: "#404040", borderColor: "#e5e5e5" }}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {filtered.map(p => (
              <button key={p.id} data-testid={`pos-add-${p.id}`} onClick={() => add(p)} disabled={p.available <= 0}
                className={`text-left rounded-md border transition-colors overflow-hidden ${p.available <= 0 ? "opacity-40 cursor-not-allowed border-neutral-200 bg-neutral-50" : "border-neutral-200 hover:border-red-600 hover:bg-red-50/30 bg-white"}`}>
                {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-24 object-cover" /> : <div className="w-full h-24 bg-neutral-100 flex items-center justify-center"><Package className="w-8 h-8 text-neutral-300" /></div>}
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase text-neutral-500 tracking-wider">{p.category}</div>
                    {p.variants?.length > 0 && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-0.5"><Layers className="w-2.5 h-2.5" /> {p.variants.length} varian</span>}
                  </div>
                  <div className="font-display font-semibold text-neutral-900 mt-1 line-clamp-2 min-h-[2.5rem] text-sm">{p.name}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.brand && <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.brand}</span>}
                    {p.size && <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.size}</span>}
                    {p.color && <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700">{p.color}</span>}
                  </div>
                  <div className="mt-2 flex items-end justify-between">
                    <div className="font-mono-tab font-bold text-sm" style={{color: "var(--brand-primary)"}}>{fmtIDR(p.daily_price)}</div>
                    <div className="text-xs text-neutral-500">Ready {p.available}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <Card className="border-neutral-200 shadow-none h-fit lg:sticky lg:top-4">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 pb-3 border-b border-neutral-200">
              <ShoppingCart className="w-4 h-4" />
              <div className="font-display font-semibold">Keranjang ({cart.length})</div>
            </div>

            <div className="space-y-3 my-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Pelanggan</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setQuickOpen(true)} data-testid="pos-quickadd-cust" className="h-6 text-xs" style={{color: "var(--brand-primary)"}}><UserPlus className="w-3 h-3 mr-1" /> Baru</Button>
                </div>
                <Input placeholder="Cari pelanggan..." value={custSearch} onChange={e => setCustSearch(e.target.value)} className="mb-1" />
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="pos-customer"><SelectValue placeholder="Pilih pelanggan" /></SelectTrigger>
                  <SelectContent>{custFiltered.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name} {c.whatsapp && `· ${c.whatsapp}`}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanggal Ambil → Kembali</Label>
                <DateRangePicker start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} testId="pos-date-range" />
              </div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Durasi: <span className="font-mono-tab font-semibold text-neutral-900">{days} hari</span></div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto pos-scroll">
              {cart.map(i => (
                <div key={i.key} className="flex items-center gap-2 py-2 border-b border-neutral-100" data-testid={`cart-item-${i.key}`}>
                  {i.image_url ? <img src={i.image_url} alt="" className="w-9 h-9 rounded object-cover shrink-0" /> : <div className="w-9 h-9 rounded bg-neutral-100 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 truncate">{i.product_name}</div>
                    {i.variant_name && <div className="text-[10px] uppercase font-bold text-blue-700">{i.variant_name}</div>}
                    <div className="text-xs text-neutral-500 font-mono-tab">{fmtIDR(i.daily_price)}/hari</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, -1)}><Minus className="w-3 h-3" /></Button>
                    <span className="w-6 text-center font-mono-tab text-sm">{i.quantity}</span>
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, 1)}><Plus className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(i.key)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
              {cart.length === 0 && <div className="text-center text-sm text-neutral-500 py-6">Belum ada item</div>}
            </div>

            <div className="mt-4">
              <Label className="text-xs">Diskon</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex border border-neutral-200 rounded-md overflow-hidden">
                  <button type="button" onClick={() => setDiscountType("rupiah")} data-testid="disc-rp" className={`px-3 text-sm font-semibold ${discountType === "rupiah" ? "text-white" : "text-neutral-600"}`} style={{background: discountType === "rupiah" ? "var(--brand-primary)" : "white"}}>Rp</button>
                  <button type="button" onClick={() => setDiscountType("persen")} data-testid="disc-pct" className={`px-3 text-sm font-semibold ${discountType === "persen" ? "text-white" : "text-neutral-600"}`} style={{background: discountType === "persen" ? "var(--brand-primary)" : "white"}}>%</button>
                </div>
                <Input data-testid="pos-discount" type="number" value={discount} onChange={e => setDiscount(e.target.value)} className="flex-1" />
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 uppercase tracking-wider">= <span className="font-mono-tab">{fmtIDR(discAmount)}</span></div>
            </div>
            <div className="mt-3">
              <DpInput total={total} value={dpValue} type={dpType} onValue={setDpValue} onType={setDpType} testIdPrefix="pos-dp" />
            </div>
            <div className="mt-3">
              <Label className="text-xs">Jaminan (Rp)</Label>
              <Input data-testid="pos-deposit" type="number" value={deposit} onChange={e => setDeposit(e.target.value)} />
            </div>
            <Textarea placeholder="Catatan (opsional)" value={notes} onChange={e => setNotes(e.target.value)} className="mt-2" />

            <div className="mt-4 pt-4 border-t border-neutral-200 space-y-1 text-sm">
              <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span className="font-mono-tab">{fmtIDR(subtotal)}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Diskon</span><span className="font-mono-tab">-{fmtIDR(discAmount)}</span></div>
              <div className="flex justify-between items-end pt-2">
                <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Total</span>
                <span className="font-display font-black text-3xl font-mono-tab" style={{color: "var(--brand-primary)"}} data-testid="pos-total">{fmtIDR(total)}</span>
              </div>
              {dp > 0 && (
                <>
                  <div className="flex justify-between text-neutral-600"><span>DP dibayar</span><span className="font-mono-tab">-{fmtIDR(dp)}</span></div>
                  <div className="flex justify-between items-end"><span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Sisa Pelunasan</span><span className="font-display font-bold text-xl font-mono-tab" data-testid="pos-remaining">{fmtIDR(remaining)}</span></div>
                </>
              )}
            </div>

            <Button data-testid="pos-checkout" onClick={() => { if (!customerId) return toast.error("Pilih atau tambahkan pelanggan dulu"); if (cart.length === 0) return toast.error("Keranjang kosong"); setDepositOpen(true); }} disabled={submitting} className="w-full mt-4 h-12 text-white text-base font-semibold disabled:opacity-70" style={{background: "var(--brand-primary)"}}>
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : <><RcpIcon className="w-4 h-4 mr-2" /> {online ? "Simpan & Cetak Struk" : "Simpan Offline & Cetak"}</>}
            </Button>
            {!online && <div className="text-[10px] text-amber-600 mt-2 text-center font-medium">Mode offline · transaksi akan tersinkron otomatis saat internet kembali</div>}
          </CardContent>
        </Card>
      </div>

      <VariantPicker product={variantProduct} open={!!variantProduct} onOpenChange={(o) => !o && setVariantProduct(null)} onPick={pushToCart} />

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Tambah Pelanggan Cepat</DialogTitle></DialogHeader>
          <form onSubmit={quickCreateCustomer} className="space-y-3">
            <div><Label>Nama <span className="text-red-600">*</span></Label>
              <Input data-testid="qc-name" required value={newCust.full_name} onChange={e => setNewCust({...newCust, full_name: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>WhatsApp</Label>
                <Input data-testid="qc-wa" placeholder="08xxxxxxxxxx" value={newCust.whatsapp} onChange={e => setNewCust({...newCust, whatsapp: e.target.value})} /></div>
              <div><Label>No. KTP/SIM</Label>
                <Input data-testid="qc-id" value={newCust.id_number} onChange={e => setNewCust({...newCust, id_number: e.target.value})} /></div>
            </div>
            <div><Label>Foto KTP/SIM</Label>
              <div className="flex items-center gap-3 mt-1">
                <input ref={custFileRef} type="file" accept="image/*" capture="environment" onChange={onCustPhoto} className="hidden" data-testid="qc-photo-input" />
                <Button type="button" variant="outline" onClick={() => custFileRef.current?.click()}><Camera className="w-4 h-4 mr-1" /> Ambil / Unggah</Button>
                {newCust.id_photo && <img src={newCust.id_photo} alt="ktp" className="h-12 rounded border border-neutral-200" />}
              </div></div>
            <div><Label>Alamat</Label>
              <Textarea value={newCust.address} onChange={e => setNewCust({...newCust, address: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Media Sosial</Label>
                <Input data-testid="qc-social" placeholder="@instagram" value={newCust.social_media} onChange={e => setNewCust({...newCust, social_media: e.target.value})} /></div>
              <div><Label>Kode Referral Teman</Label>
                <Input data-testid="qc-referral" placeholder="Opsional" value={newCust.referred_by_code} onChange={e => setNewCust({...newCust, referred_by_code: e.target.value.toUpperCase()})} /></div>
            </div>
            <div><Label>Link Google Maps</Label>
              <Input data-testid="qc-maps" placeholder="https://maps.app.goo.gl/..." value={newCust.maps_link} onChange={e => setNewCust({...newCust, maps_link: e.target.value})} /></div>
            <div><Label>Tahu {settings?.business_name || "kami"} dari mana? <span className="text-red-600">*</span></Label>
              <Select value={newCust.acquisition_source} onValueChange={v => setNewCust({...newCust, acquisition_source: v})}>
                <SelectTrigger data-testid="qc-source"><SelectValue placeholder="Pilih sumber" /></SelectTrigger>
                <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button data-testid="qc-save" type="submit" disabled={custSubmitting} className="w-full text-white disabled:opacity-70" style={{background: "var(--brand-primary)"}}>{custSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan & Pilih"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
        <DialogContent className="max-w-md" data-testid="deposit-reminder-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" style={{color: "var(--brand-primary)"}} /> Minta Jaminan</DialogTitle></DialogHeader>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="deposit-reminder-text">
            {settings?.deposit_reminder_text || "Jangan lupa minta jaminan/KTP dari pelanggan sebelum barang dibawa."}
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setDepositOpen(false)} data-testid="deposit-cancel">Batal</Button>
            <Button className="flex-1 text-white" style={{background: "var(--brand-primary)"}} onClick={() => { setDepositOpen(false); doCheckout(); }} data-testid="deposit-continue">Lanjut &amp; Cetak</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
